import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import type { SketchFace, SketchVec3 } from './types';
import { planeBasis } from './triangulate';

/**
 * Face splitting — Sketch's core behavior when drawing ON a face.
 *
 * Segments drawn in the face's plane (closing against the contour, a hole,
 * or earlier interior segments) partition the face into new faces. The
 * algorithm is a planar-graph face walk in 2D:
 *   1. project the face plane into (u, v),
 *   2. collect all edges (contour + holes + interior segments), splitting
 *      every pairwise crossing,
 *   3. half-edge traversal (next = CCW predecessor of the reversed edge)
 *      enumerates every closed loop exactly once,
 *   4. positive-winding loops become outer contours; negative loops from a
 *      DIFFERENT connected component nested inside one become its holes (the
 *      ring case: a circle drawn inside a rectangle yields ring + disc).
 *      Same-component negative cycles are the component's exterior face or
 *      its own mirror copy and are discarded — nesting them would self-hole
 *      the face (the structural corruption this guards against).
 *
 * All loops stay OPEN (first point not repeated) per types.ts.
 */

const EPS = 1e-6;

interface P2 { x: number; y: number }

const sub = (a: P2, b: P2): P2 => ({ x: a.x - b.x, y: a.y - b.y });
const cross2 = (a: P2, b: P2): number => a.x * b.y - a.y * b.x;
/** Signed area of a closed 2D loop (positive = CCW). */
export const signedArea = (loop: P2[]): number => {
    let area = 0;
    for (let i = 0; i < loop.length; i++) {
        area += cross2(loop[i], loop[(i + 1) % loop.length]);
    }
    return area / 2;
};

/** Intersection of two segments with parameters along each, or null. */
function intersectEdges(e1: { a: P2; b: P2 }, e2: { a: P2; b: P2 }): { x: number; y: number; t: number; u: number } | null {
    const r = sub(e1.b, e1.a);
    const s = sub(e2.b, e2.a);
    const denom = cross2(r, s);
    if (Math.abs(denom) < EPS * EPS) return null;
    const qp = sub(e2.a, e1.a);
    const t = cross2(qp, s) / denom;
    const u = cross2(qp, r) / denom;
    if (t < -EPS || t > 1 + EPS || u < -EPS || u > 1 + EPS) return null;
    return { x: e1.a.x + r.x * t, y: e1.a.y + r.y * t, t, u };
}

/** Split all edges at pairwise crossings until the set is node-simple.
 *  Extra fields on the edge records (e.g. provenance ids) are preserved. */
export function splitEdges<T extends { a: P2; b: P2; interior: boolean }>(edges: T[]): T[] {
    let out = edges.map((e) => ({ ...e }));
    let changed = true;
    let guard = 0;
    // Each round performs at least one REAL split, so the round count is
    // bounded by the number of crossings (< n²/2).
    const maxRounds = out.length * out.length + 16;
    while (changed && guard++ < maxRounds) {
        changed = false;
        for (let i = 0; i < out.length && !changed; i++) {
            for (let j = i + 1; j < out.length && !changed; j++) {
                const hit = intersectEdges(out[i], out[j]);
                if (!hit) continue;
                // Only INTERIOR crossings split an edge. Hits at shared
                // endpoints (adjacent contour edges, T-junction anchors)
                // are already node-simple; counting them as changes
                // restarted the scan forever and starved real crossings,
                // leaving crossing strokes unsplit (corrupt faces).
                const iInterior = hit.t > EPS && hit.t < 1 - EPS;
                const jInterior = hit.u > EPS && hit.u < 1 - EPS;
                if (!iInterior && !jInterior) continue;
                const e1 = out[i];
                const e2 = out[j];
                const pieces = (e: T, interior: boolean): T[] => {
                    if (!interior) return [e];
                    const halves: T[] = [];
                    if (Math.hypot(hit.x - e.a.x, hit.y - e.a.y) > EPS) halves.push({ ...e, a: e.a, b: hit });
                    if (Math.hypot(e.b.x - hit.x, e.b.y - hit.y) > EPS) halves.push({ ...e, a: hit, b: e.b });
                    return halves.length > 0 ? halves : [e];
                };
                out = [
                    ...out.slice(0, i), ...pieces(e1, iInterior), ...out.slice(i + 1, j),
                    ...pieces(e2, jInterior), ...out.slice(j + 1),
                ];
                changed = true;
            }
        }
    }
    return out;
}

interface EdgeRecord { a: P2; b: P2; /** True when the edge belongs to an existing hole contour. */ hole?: boolean }

interface HalfEdge { from: P2; to: P2; fromKey: string; toKey: string; src: EdgeRecord }

const keyOf = (p: P2): string => `${Math.round(p.x / EPS)}|${Math.round(p.y / EPS)}`;

/** One enumerated loop: its points plus the edge records it traverses. */
export interface WalkedLoop {
    points: P2[];
    edges: EdgeRecord[];
}

/** Enumerate all closed loops (both orientations) of the edge set via half-edge traversal. */
export function walkLoops<T extends EdgeRecord>(edges: T[]): WalkedLoop[] {
    const outgoing = new Map<string, HalfEdge[]>();
    const register = (from: P2, to: P2, src: EdgeRecord): void => {
        const h: HalfEdge = { from, to, fromKey: keyOf(from), toKey: keyOf(to), src };
        const list = outgoing.get(h.fromKey) ?? [];
        list.push(h);
        outgoing.set(h.fromKey, list);
    };
    edges.forEach((e) => {
        register(e.a, e.b, e);
        register(e.b, e.a, e);
    });
    // Angular sort around each vertex.
    outgoing.forEach((list) => {
        list.sort((h1, h2) =>
            Math.atan2(h1.to.y - h1.from.y, h1.to.x - h1.from.x)
            - Math.atan2(h2.to.y - h2.from.y, h2.to.x - h2.from.x));
    });

    const used = new Set<HalfEdge>();
    const nextHalfEdge = (h: HalfEdge): HalfEdge => {
        // next = CCW-list predecessor of the reversed edge around h.to
        // (standard planar next rule; verified on the unit square walk).
        const list = outgoing.get(h.toKey) ?? [];
        const reversed = list.find((c) => c.toKey === h.fromKey && keyOf(c.to) === h.fromKey);
        if (!reversed) return list[0];
        const idx = list.indexOf(reversed);
        return list[(idx - 1 + list.length) % list.length];
    };

    const loops: WalkedLoop[] = [];
    outgoing.forEach((list) => {
        for (const start of list) {
            if (used.has(start)) continue;
            const points: P2[] = [];
            const loopEdges: EdgeRecord[] = [];
            let h: HalfEdge = start;
            let guard = 0;
            while (guard++ < 2048) {
                used.add(h);
                points.push(h.from);
                loopEdges.push(h.src);
                h = nextHalfEdge(h);
                if (h === start) break;
            }
            if (points.length >= 3) loops.push({ points, edges: loopEdges });
        }
    });
    return loops;
}

/** Point-in-polygon (ray cast) for hole nesting. */
export function pointInside(p: P2, polygon: P2[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;
        const intersect = (yi > p.y) !== (yj > p.y)
            && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

export interface HoleNesting {
    /** Index into the `positive` loop array that receives the hole. */
    posIdx: number;
    /** The negative (CW) loop, in the caller's coordinate space. */
    hole: P2[];
}

/**
 * Nest negative (CW) loops as holes of the smallest positive loop that
 * contains them AND lives in a DIFFERENT connected component (`compOf`
 * labels loops). Same-component CW cycles are the component's exterior face
 * or the mirror copy of its own boundary (disconnected components walk out
 * both orientations of every cycle) — nesting those would punch a
 * loop-shaped hole through the face itself. The probe is the CW loop's own
 * first vertex: splitEdges guarantees different components share NO points,
 * so the vertex never lies on a candidate's boundary and the even-odd cast
 * is exact (boundary parity was the corruption source of the old probe).
 */
export function nestHoles(
    positive: P2[][],
    negative: P2[][],
    compOf: (loop: P2[]) => unknown
): HoleNesting[] {
    const nestings: HoleNesting[] = [];
    for (const neg of negative) {
        const negComp = compOf(neg);
        let bestIdx = -1;
        let bestArea = Infinity;
        positive.forEach((pos, i) => {
            if (compOf(pos) === negComp) return;
            if (pointInside(neg[0], pos)) {
                const area = signedArea(pos);
                if (area < bestArea) {
                    bestArea = area;
                    bestIdx = i;
                }
            }
        });
        if (bestIdx >= 0) nestings.push({ posIdx: bestIdx, hole: neg });
    }
    return nestings;
}

/** Union-find over vertex keys → a representative per connected component. */
export function loopComponentLabeler(edges: Array<{ a: P2; b: P2 }>): (loop: P2[]) => string {
    const parent = new Map<string, string>();
    const find = (k: string): string => {
        let root = k;
        while (parent.get(root) !== root) root = parent.get(root)!;
        let cur = k;
        while (parent.get(cur) !== root) {
            const next = parent.get(cur)!;
            parent.set(cur, root);
            cur = next;
        }
        return root;
    };
    edges.forEach((e) => {
        const ka = keyOf(e.a);
        const kb = keyOf(e.b);
        if (!parent.has(ka)) parent.set(ka, ka);
        if (!parent.has(kb)) parent.set(kb, kb);
        const ra = find(ka);
        const rb = find(kb);
        if (ra !== rb) parent.set(ra, rb);
    });
    return (loop: P2[]) => find(keyOf(loop[0]));
}

export interface FaceSplitResult {
    ok: boolean;
    faces: SketchFace[];
    /** Segments echoed back for etching/inference even when nothing split. */
    etches: SketchVec3[][];
    reason?: string;
}

/**
 * Split `face` with interior segments (world-space, in the face's plane).
 * Segments that close no region leave the face unchanged (etch-only).
 */
export function splitFaceWithSegments(
    face: SketchFace,
    segments: SketchVec3[][]
): FaceSplitResult {
    const n = new THREE.Vector3(...face.normal).normalize();
    const origin = new THREE.Vector3(...face.origin);
    const { u, v } = planeBasis(n);
    const project = (loop: SketchVec3[]): P2[] =>
        loop.map((p) => {
            const d = new THREE.Vector3(...p).sub(origin);
            return { x: d.dot(u), y: d.dot(v) };
        });

    const edges: Array<{ a: P2; b: P2; interior: boolean; hole?: boolean }> = [];
    const addLoop = (loop: SketchVec3[], interior: boolean, hole = false): void => {
        const pts = project(loop);
        for (let i = 0; i < pts.length; i++) {
            edges.push({ a: pts[i], b: pts[(i + 1) % pts.length], interior, hole });
        }
    };
    addLoop(face.outer, false);
    face.holes.forEach((h) => addLoop(h, false, true));
    for (const seg of segments) {
        const pts = project(seg);
        for (let i = 0; i < pts.length - 1; i++) {
            edges.push({ a: pts[i], b: pts[i + 1], interior: true });
        }
    }

    const simple = splitEdges(edges);
    const loops = walkLoops(simple);
    const positive = loops.filter((l) => signedArea(l.points) > EPS);
    const negative = loops.filter((l) => signedArea(l.points) < -EPS);

    // A positive cycle built ENTIRELY of existing-hole edges is the hole's
    // own mirror — the hole region must stay empty (no face) and it must not
    // serve as a nesting container. Its CW twin still nests below as the
    // hole of whatever encloses it, so re-splitting a ring face keeps the
    // ring holed.
    const isHoleMirror = (l: WalkedLoop): boolean => l.edges.every((e) => e.hole === true);
    const faceLoops = positive.filter((l) => !isHoleMirror(l));

    if (faceLoops.length === 0) {
        return { ok: false, faces: [face], etches: segments, reason: 'No closed regions.' };
    }

    const lift = (p: P2): SketchVec3 => [
        origin.x + u.x * p.x + v.x * p.y,
        origin.y + u.y * p.x + v.y * p.y,
        origin.z + u.z * p.x + v.z * p.y,
    ];

    const newFaces: SketchFace[] = faceLoops.map((loop) => {
        const outer = loop.points.map(lift);
        return {
            id: uuidv4(),
            origin: outer[0],
            normal: [...face.normal] as SketchVec3,
            outer,
            holes: [] as SketchVec3[][],
            materialId: face.materialId,
        };
    });

    // Nest negative loops as holes of containing face loops from a different
    // connected component (see nestHoles for the mirror/exterior rule).
    const compOf = loopComponentLabeler(simple);
    for (const { posIdx, hole } of nestHoles(
        faceLoops.map((l) => l.points),
        negative.map((l) => l.points),
        compOf
    )) {
        newFaces[posIdx].holes.push(hole.map(lift));
    }

    return { ok: true, faces: newFaces, etches: segments };
}
